/**
 * Estado do usuário guardado no navegador — favoritos, histórico, preferências.
 * (As anotações moraram aqui; hoje ficam na conta, e `peekLocal`/`removeLocal`
 * servem para levá-las ao servidor uma única vez.)
 *
 * Provisório por natureza: serve enquanto o back não tem onde persistir.
 * Não sincroniza entre computadores. As chaves sempre incluem o id do
 * usuário, para duas pessoas no mesmo navegador não verem os dados uma da
 * outra.
 */
import { useCallback, useSyncExternalStore } from 'react';

const listeners = new Set<() => void>();
const cache = new Map<string, unknown>();
const PREFIX = 'centralone:';

function read<T>(key: string, fallback: T, valid: (v: unknown) => v is T): T {
  if (cache.has(key)) return cache.get(key) as T;
  let value = fallback;
  try {
    const raw = localStorage.getItem(PREFIX + key);
    const parsed: unknown = raw === null ? fallback : JSON.parse(raw);
    if (valid(parsed)) value = parsed;
  } catch {
    // Armazenamento bloqueado ou corrompido: começa do padrão.
  }
  cache.set(key, value);
  return value;
}

function write(key: string, value: unknown) {
  cache.set(key, value);
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    // Sem armazenamento, o valor vale só nesta sessão.
  }
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const isStringArray = (v: unknown): v is string[] =>
  Array.isArray(v) && v.every((x) => typeof x === 'string');
const EMPTY: string[] = [];

/** Um conjunto de ids (favoritos, por exemplo). */
export function useLocalSet(key: string) {
  const ids = useSyncExternalStore(subscribe, () => read(key, EMPTY, isStringArray));
  const toggle = useCallback(
    (id: string) => {
      const current = read(key, EMPTY, isStringArray);
      write(key, current.includes(id) ? current.filter((x) => x !== id) : [...current, id]);
    },
    [key],
  );
  const has = useCallback((id: string) => ids.includes(id), [ids]);
  return { ids, has, toggle };
}

/**
 * Um valor JSON qualquer, validado na leitura — o que está no navegador
 * pode ter sido gravado por outra versão do app.
 */
export function useLocalValue<T>(key: string, fallback: T, valid: (v: unknown) => v is T) {
  const value = useSyncExternalStore(subscribe, () => read(key, fallback, valid));
  const set = useCallback((next: T) => write(key, next), [key]);
  /** Atualiza a partir do valor gravado agora — seguro dentro de efeitos. */
  const update = useCallback(
    (fn: (current: T) => T) => write(key, fn(read(key, fallback, valid))),
    [key, fallback, valid],
  );
  return [value, set, update] as const;
}

/**
 * Lê um valor gravado sem assinar mudanças — para levar ao servidor o que
 * ficou no navegador. `null` se não há nada, se é inválido ou se o
 * armazenamento está bloqueado.
 */
export function peekLocal<T>(key: string, valid: (v: unknown) => v is T): T | null {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    return valid(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Apaga um valor do navegador (e da memória). Sem armazenamento, não faz nada. */
export function removeLocal(key: string) {
  cache.delete(key);
  try {
    localStorage.removeItem(PREFIX + key);
  } catch {
    // Armazenamento bloqueado: não havia o que apagar.
  }
  listeners.forEach((l) => l());
}
